import React from 'react';
import styled from 'styled-components';

import { animations, colors, media } from '../../../core';
import { getAppLinksForUser } from '../../../core/utils';
import Icon from '../Icon';
import Link from '../Link';
import Text from '../Text';

interface MenuProps {
  isNavOpen: boolean;
  user: UserProps;
  onClose?: () => void;
  children?: React.ReactNode;
}

interface NavMenuProps {
  open?: boolean;
}

interface LinkProps {
  url: string;
  label: string;
}

export interface UserProps {
  firstname: string;
  lastname: string;
  roles: string[];
  initials?: string;
}

interface UserDropdownProps {
  handleNavAnchorClick: () => void;
  user: UserProps;
  isNavOpen: boolean;
  showUnderline?: boolean;
}

const NavAnchor = styled.button<{ showUnderline?: boolean }>`
  -webkit-appearance: none;
  align-items: center;
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.userDropdownColor || colors.primary};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;

  ${Text} {
    color: inherit;
    margin-right: 4px;
    margin-bottom: 0;
  }

  path {
    fill: ${({ theme }) => theme.userDropdownColor || colors.primary};
  }

  &:hover {
    color: ${({ theme }) => theme.userDropdownColorHover || colors.primaryHover};

    path {
      fill: ${({ theme }) => theme.userDropdownColorHover || colors.primaryHover};
    }
  }

  &:hover,
  &:focus,
  &:active {
    outline: 0;

    ${Text} {
      text-decoration: ${({ showUnderline }) => (showUnderline ? 'underline' : 'none')};
    }
  }
`;

const NavMenu = styled.nav<NavMenuProps>`
  animation: ${animations.fadeAndSlideInFromTop} 0.2s ease-out forwards;
  background-color: ${colors.white};
  border-radius: 3px;
  box-shadow: 1px 1px 6px 2px rgba(225, 225, 225, 0.5);
  display: ${({ open }) => (open ? 'block' : 'none')};
  margin-top: 8px;
  padding: 24px 16px 16px;
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  z-index: ${({ open }) => (open ? 5 : -5)};

  ${media.tabletPortrait} {
    padding: 0 0 24px;
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    box-shadow: none;
    border-radius: 0;
    overflow: scroll;
  }
`;

const NavHeader = styled(Text)`
  margin-bottom: 0;
  letter-spacing: 0.5px;
  font-size: ${({ theme }) => theme.headingOutline || '12px'};
  line-height: ${({ theme }) => theme.headingOutlineLineHeight || '1.1'};
  text-transform: uppercase;

  ${media.tabletPortrait} {
    font-size: 20px;
    padding: 20px 12px;
    text-transform: capitalize;
  }
`;

const NavList = styled.ul`
  list-style-type: none;
  padding-left: 10px;
  margin: 8px 0 16px;

  &:last-child {
    margin-bottom: 0;

    ${media.tabletPortrait} {
      border: 0;
    }
  }

  ${media.tabletPortrait} {
    margin: 0;
    border-top: 1px solid ${colors.cloud};
    border-bottom: 1px solid ${colors.cloud};
  }
`;

const NavItem = styled.li`
  padding: 0;
  margin: 0;
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.fontColor || colors.ash};
  display: block;
  font-size: ${({ theme }) => theme.fontSize || '14px'};
  line-height: ${({ theme }) => theme.lineHeight || 'normal'};
  font-weight: ${({ theme }) => theme.fontWeight || 600};
  padding: 8px 0;
  margin: 0;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.navbarColorHover || colors.gray};
    transition: 0.1s ease-in-out color;
    text-decoration: underline;
  }

  ${media.tabletPortrait} {
    font-size: 16px;
    padding: 2.2vh 0 2.2vh 36px;
  }
`;

const MenuHeader = styled.div`
  display: none;

  ${media.tabletPortrait} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 20px 10px 0;
  }
`;

const MenuIcon = styled(Icon)`
  color: ${({ theme }) => theme.userDropdownColor || colors.primary};
  margin: 6px 0 0 4px;
`;

const MenuText = styled(Text)`
  color: ${({ theme }) => theme.userDropdownColor || colors.primary};
`;

export const IconButton = styled.button`
  background: none;
  outline: none;
  border: 0;
  padding: 0;
`;

export const Menu = ({ isNavOpen, user, onClose, children }: MenuProps) => {
  const userAppLinks = getAppLinksForUser(user);

  return (
    <NavMenu open={isNavOpen}>
      <MenuHeader>
        <IconButton onClick={onClose}>
          <MenuIcon type="close" onClick={onClose} />
        </IconButton>
      </MenuHeader>

      {children}

      <NavHeader size="medium">Apps</NavHeader>

      <NavList>
        {userAppLinks.map((link: LinkProps) => (
          <NavItem key={link.label}>
            <NavLink href={link.url}>{link.label}</NavLink>
          </NavItem>
        ))}
      </NavList>

      <NavHeader size="medium">
        {user.firstname} {user.lastname}
      </NavHeader>

      <NavList>
        <NavItem>
          <NavLink href="/user/profile">Profile</NavLink>
        </NavItem>

        <NavItem>
          <NavLink href="/logout">Log Out</NavLink>
        </NavItem>
      </NavList>
    </NavMenu>
  );
};

const UserDropdown = ({
  isNavOpen,
  handleNavAnchorClick,
  user,
  showUnderline = false,
}: UserDropdownProps) => (
  <React.Fragment>
    <NavAnchor onClick={handleNavAnchorClick} showUnderline={showUnderline}>
      <MenuText>{user?.initials ? user.initials : `${user?.firstname} ${user?.lastname}`}</MenuText>

      <MenuIcon type="caret-down" size={12} onClick={handleNavAnchorClick} />
    </NavAnchor>
    <Menu isNavOpen={isNavOpen} user={user} onClose={handleNavAnchorClick} />
  </React.Fragment>
);
export default UserDropdown;
